import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { CopyButton, StatusBadge, fmtDate } from "@/components/shared";
import { TwoFactorCard } from "./TwoFactorCard";
import { TrophyBadge } from "./ReferralLeaderboard";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [mobile, setMobile] = useState(user?.mobile || "");
  const [loading, setLoading] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.put("/profile", { full_name: fullName, mobile });
      setUser(r.data);
      toast.success("Profile updated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const readonly = [
    ["Email", user?.email], ["User ID", user?.id], ["Referral Code", user?.referral_code], ["Registration Date", fmtDate(user?.created_at)],
  ];

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div>
        <p className="label-eyebrow text-xs font-mono uppercase tracking-[0.2em] text-cyan-400">Profile</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mt-1">Account Details</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="glass rounded-2xl p-6 text-center">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-slate-950 font-heading font-bold text-3xl mx-auto mb-4">{user?.full_name?.[0]?.toUpperCase()}</div>
          <h3 className="font-heading text-lg text-white">{user?.full_name}</h3>
          <p className="text-sm text-slate-500">{user?.email}</p>
          <div className="mt-4"><StatusBadge status={user?.account_status} /></div>
          {user?.awards?.length > 0 && (
            <div className="mt-5 pt-4 border-t border-white/5" data-testid="profile-awards">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Referral Trophies</p>
              <div className="flex flex-wrap justify-center gap-2">
                {user.awards.map((a) => <TrophyBadge key={a.month} rank={a.rank} label={a.month_label} testId={`profile-award-${a.month}`} />)}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 glass rounded-2xl p-6">
          <form onSubmit={save} className="space-y-4">
            <h3 className="font-heading text-white mb-2">Editable Information</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-sm text-slate-300 mb-1.5 block">Full Name</label>
                <input data-testid="profile-fullname" value={fullName} onChange={(e) => setFullName(e.target.value)} className="w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 focus:border-cyan-400 focus:outline-none" /></div>
              <div><label className="text-sm text-slate-300 mb-1.5 block">Mobile Number</label>
                <input data-testid="profile-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} className="w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 focus:border-cyan-400 focus:outline-none" /></div>
            </div>
            <button type="submit" disabled={loading} data-testid="profile-save" className="btn-glow px-6 py-2.5 rounded-lg font-semibold text-slate-950 inline-flex items-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes
            </button>
          </form>

          <div className="border-t border-white/5 mt-6 pt-6 space-y-3">
            <h3 className="font-heading text-white mb-2">Account Information</h3>
            {readonly.map(([label, val]) => (
              <div key={label} className="flex items-center justify-between gap-3">
                <span className="text-sm text-slate-500">{label}</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-slate-200 truncate max-w-[200px]">{val}</span>
                  {label === "Referral Code" && <CopyButton value={val} testId="profile-code-copy" />}
                </div>
              </div>
            ))}
          </div>
        </div>

        <TwoFactorCard />
        <ChangePasswordCard className="lg:col-span-3" />
      </div>
    </div>
  );
}
